import { create } from 'zustand';
import { getStoredUser, getAccessToken, clearTokens, saveTokens } from '../api/client';
import { googleLogin, logout as apiLogout, fetchMe, type AuthResponse } from '../api/auth';

type User = AuthResponse['user'];

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;

  /** Hydrate from sessionStorage on app start */
  hydrate: () => void;

  /** Exchange Google ID token for JWT session */
  loginWithGoogle: (idToken: string) => Promise<void>;

  /** Refresh user profile from backend */
  refreshUser: () => Promise<void>;

  /** Logout + clear tokens */
  logout: () => Promise<void>;

  /** True if session tokens exist */
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  error: null,

  hydrate() {
    const token = getAccessToken();
    if (!token) {
      set({ user: null, loading: false });
      return;
    }
    const stored = getStoredUser() as User | null;
    set({ user: stored, loading: false });
  },

  async loginWithGoogle(idToken: string) {
    set({ loading: true, error: null });
    try {
      const data = await googleLogin(idToken);
      set({ user: data.user as User, loading: false });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      set({ error: msg, loading: false });
      throw err;
    }
  },

  async refreshUser() {
    try {
      const user = await fetchMe();
      set({ user });
      // Keep sessionStorage in sync
      const stored = {
        accessToken: getAccessToken()!,
        refreshToken: sessionStorage.getItem('ipv_refresh')!,
        user: user as unknown as Record<string, unknown>,
      };
      saveTokens(stored);
    } catch {
      // silently ignore – token may have expired
    }
  },

  async logout() {
    try {
      const rt = sessionStorage.getItem('ipv_refresh') ?? undefined;
      await apiLogout(rt);
    } finally {
      clearTokens();
      set({ user: null, error: null });
    }
  },

  isAuthenticated() {
    return !!getAccessToken();
  },
}));
